import { ref, watch, computed } from '@vue/composition-api'
import store from '@/shared/store'
import i18n from '@hw-core/libs/i18n'

// Notification
import { useToast } from 'vue-toastification/composition'
import { COLOR_VARIANTS } from '@/shared/constants/colors'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'
import { useRouter } from '@hw-ui/utils/utils'
import * as moment from 'moment'

import { onFetchingList } from '@/shared/services/users'

export default function useUsersList() {
    // Use toast
    const toast = useToast()

    // Use router
    const { route, router } = useRouter()

    const refUserListTable = ref(null)

    // Table Handlers
    const hoiwaColumns = ref([
        {
            key: 'created',
            label: i18n.t('application.steps.applied'),
            sortable: false,
            style: {
                width: '130px',
                maxWidth: '130px',
            },
        },
        {
            key: 'user',
            label: i18n.t('user'),
            sortable: false,
            style: {
                width: '200px',
                maxWidth: '300px',
            },
        },
        {
            key: 'mobile',
            label: i18n.t('mobile'),
            sortable: false,
            style: {
                width: '150px',
                maxWidth: '250px',
            },
        },
        {
            key: 'email',
            label: i18n.t('email'),
            sortable: false,
            style: {
                width: '250px',
                maxWidth: '350px',
            },
        },
        {
            key: 'countUpdates',
            label: '',
            style: {
                width: '80px',
                maxWidth: '80px',
            },
        },
        {
            key: 'profession',
            label: i18n.t('profession'),
            sortable: false,
            style: {
                width: '160px',
                maxWidth: '160px',
            },
        },
        {
            key: 'application.seeking',
            label: i18n.t('application.filters.seeking'),
            style: {
                width: '150px',
                maxWidth: '150px',
            },
        },
        {
            key: 'application.start',
            label: i18n.t('start'),
            style: {
                width: '150px',
                maxWidth: '150px',
            },
        },
        {
            key: 'application.end',
            label: i18n.t('end'),
            style: {
                width: '150px',
                maxWidth: '150px',
            },
        },
        {
            key: 'love',
            sortable: false,
            style: {
                width: '100px',
                maxWidth: '100px',
            },
        },
        {
            key: 'unresolvedComments',
            label: '',
            sortable: false,
            style: {
                width: '90px',
                maxWidth: '90px',
            },
        },
        {
            key: 'applicationUpdates',
            label: i18n.t('last_update'),
            sortable: false,
            style: {
                width: '260px',
                maxWidth: '260px',
            },
        },
        {
            key: 'status',
            label: i18n.t('status'),
            sortable: false,
            style: {
                width: '130px',
                maxWidth: '130px',
            },
        },
        {
            key: 'actions',
            label: i18n.t('actions'),
            style: {
                width: '120px',
                maxWidth: '120px',
            },
        },
    ])
    const oiwaColumns = ref([
        {
            key: 'created',
            label: i18n.t('application.steps.applied'),
            sortable: false,
            style: {
                width: '130px',
            }
        },
        {
            key: 'user',
            label: i18n.t('user'),
            sortable: false,
        },
        {
            key: 'email',
            label: i18n.t('email'),
            sortable: false,
        },
        {
            key: 'mobile',
            label: i18n.t('mobile'),
            sortable: false,
            style: {
                width: '140px',
            }
        },
        {
            key: 'profession',
            label: i18n.t('profession'),
            sortable: false,
        },
        {
            key: 'applicationUpdates',
            label: i18n.t('last_update'),
            sortable: false,
        },
        {
            key: 'actions',
            label: i18n.t('actions'),
        },
    ])
    const totalRows = ref(0)
    const perPage = ref(route.value.query.limit ? +route.value.query.limit : 10)
    const totalUsers = ref(0)
    const currentPage = ref(route.value.query.page ? +route.value.query.page : 1)
    const perPageOptions = [10, 25, 50, 100]
    const searchQuery = ref(route.value.query.q ? route.value.query.q : '')
    const sortBy = ref('id')
    const isSortDirDesc = ref(true)
    const roleFilter = ref(null)
    const planFilter = ref(route.value.query.plan ? route.value.query.plan : null)
    const statusFilter = ref(route.value.query.status ? route.value.query.status : 'pending')
    const stateFilter = ref(route.value.query.state ? route.value.query.state : '')
    const professionFilter = ref(route.value.query.role ? route.value.query.role : '')
    const stepFilter = ref(route.value.query.step ? route.value.query.step : '')
    const oiwaFilter = ref(route.value.query.oiwa ? route.value.query.oiwa : '')
    const residencyFilter = ref('finland')
    const seekingFilter = ref(route.value.query.seeking ? route.value.query.seeking : '')
    const seekingStartFilter = ref(
        route.value.query.seekingStart && moment(new Date(+route.value.query.seekingStart)).isValid()
            ? new Date(moment(new Date(+route.value.query.seekingStart)).format('YYYY-MM-DD 00:00:00'))
            : ''
    )
    const seekingEndFilter = ref(
        route.value.query.seekingEnd && moment(new Date(+route.value.query.seekingEnd)).isValid()
            ? new Date(moment(new Date(+route.value.query.seekingEnd)).format('YYYY-MM-DD 23:59:59'))
            : ''
    )

    const cityFilter = ref(route.value.query.city ? route.value.query.city.split(',') : [])
    const groupFilter = ref(route.value.query.group ? route.value.query.group : null)
    const ageFilter = ref(null)
    const startAfter = ref(route.value.query.startAfter ? route.value.query.startAfter : null)
    const endBefore = ref(route.value.query.endBefore ? route.value.query.endBefore : null)
    const loadMore = ref(true)
    const canLoadMore = ref(false)
    const countApplicationRequest = ref(0)
    const actionChangePage = ref('')
    let users = []
    let deleteId = null

    const dataMeta = computed(() => {
        const localItemsCount = refUserListTable.value ? refUserListTable.value.localItems.length : 0
        return {
            from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: totalUsers.value,
        }
    })

    const refetchData = () => {
        const query = {}
        if (residencyFilter.value) {
            query.residency = residencyFilter.value
        }
        if (planFilter.value) {
            query.plan = planFilter.value
        }
        if (statusFilter.value) {
            query.status = statusFilter.value
        }
        if (stateFilter.value) {
            query.state = stateFilter.value
        }
        if (cityFilter.value.length > 0) {
            query.city = cityFilter.value.join(',')
        }
        if (groupFilter.value) {
            query.group = groupFilter.value
        }
        if (professionFilter.value) {
            query.role = professionFilter.value
        }
        if (stepFilter.value) {
            query.step = stepFilter.value
        }
        if (searchQuery.value) {
            query.q = searchQuery.value
        }
        if (oiwaFilter.value && oiwaFilter.value.length > 0) {
            query.oiwa = oiwaFilter.value
        }
        if (seekingFilter.value && seekingFilter.value.length > 0) {
            query.seeking = seekingFilter.value
            if (seekingStartFilter.value) {
                query.seekingStart = seekingStartFilter.value.getTime()
            }
            if (seekingEndFilter.value) {
                query.seekingEnd = seekingEndFilter.value.getTime()
            }
        }
        if (users && users.length > 0) {
            if (actionChangePage.value === 'next') {
                query.startAfter = users[users.length - 1].created.toDate().getTime()
            } else if (actionChangePage.value === 'prev') {
                query.endBefore = users[0].created.toDate().getTime()
            }
        }
        if (currentPage.value) {
            query.page = currentPage.value
        }
        if (perPage.value) {
            query.limit = perPage.value
        }

        router.replace(
            {
                query,
            },
            () => {}
        )
        refUserListTable.value.refresh()
    }

    const resetQueryParams = () => {
        perPage.value = 10
        currentPage.value = 1
        searchQuery.value = ''
        planFilter.value = null
        statusFilter.value = 'pending'
        stateFilter.value = ''
        professionFilter.value = ''
        stepFilter.value = ''
        oiwaFilter.value = ''
        cityFilter.value = []
        groupFilter.value = null
        residencyFilter.value = 'finland'
    }

    const changePage = action => {
        if (action === 'prev' && currentPage.value > 1) {
            currentPage.value -= 1
            actionChangePage.value = 'prev'
        }
        if (action === 'next' && canLoadMore.value) {
            currentPage.value += 1
            actionChangePage.value = 'next'
        }
    }

    const changePerPage = value => {
        perPage.value = value
        currentPage.value = 1
    }

    watch(
        [
            currentPage,
            searchQuery,
            roleFilter,
            planFilter,
            statusFilter,
            stateFilter,
            cityFilter,
            groupFilter,
            professionFilter,
            stepFilter,
            oiwaFilter,
            seekingFilter,
            seekingStartFilter,
            seekingEndFilter,
            ageFilter,
            residencyFilter,
        ],
        (newVal, oldVal) => {
            if (newVal[0] === oldVal[0]) {
                users = []
            }
            refetchData()
        }
    )

    watch(route, to => {
        if (Object.keys(to.query).length === 0) {
            resetQueryParams()
        }
    })

    const deleteUser = id => {
        deleteId = id
    }

    const confirmDelete = () => {
        store
            .dispatch('appApplications/deleteApplication', { id: deleteId })
            .then(() => {
                users = []
                refetchData()
                toast({
                    component: ToastificationContent,
                    props: {
                        title: 'User deleted',
                        icon: 'CheckIcon',
                        variant: 'success',
                    },
                })
            })
            .catch(() => {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: i18n.t('error'),
                        text: i18n.t('something_went_wrong'),
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                    },
                })
            })
    }
    const fetchApplications = async () => {
        // fetchApplicationsProvider fn
        const fetchApplicationsAction = 'appApplications/fetchApplications'
        const queryParams = {
            q: searchQuery.value,
            perPage: perPage.value,
            page: currentPage.value,
            sortBy: sortBy.value,
            sortDesc: isSortDirDesc.value,
            role: roleFilter.value,
            state: stateFilter.value,
            city: cityFilter.value,
            plan: planFilter.value,
            status: statusFilter.value,
            group: groupFilter.value,
            profession: professionFilter.value,
            step: stepFilter.value,
            seeking: seekingFilter.value,
            oiwa: oiwaFilter.value,
            seekingStart: seekingStartFilter.value,
            seekingEnd: seekingEndFilter.value,
            age: ageFilter.value,
            residency: residencyFilter.value,
            list: users,
            actionChangePage: actionChangePage.value,
            startAfter: startAfter.value,
            endBefore: endBefore.value,
        }
        
        const { data, isMore } = await onFetchingList(
            // TODO no need to use await here
            store,
            toast,
            fetchApplicationsAction,
            queryParams
        )
        if (data && data.length > 0) {
            users = data // need to assign data to users here
            canLoadMore.value = isMore
            loadMore.value = false
        }
        return users
    }

    const fetchStatistics = ({ callback }) => {
        store.dispatch('appApplications/fetchStatistics', { callback })
    }

    const fetchApplicationRequests = () => {
        store
            .dispatch('appApplications/fetchApplicationRequests')
            .then(size => {
                countApplicationRequest.value = size
            })
            .catch(() => {
                countApplicationRequest.value = 0
            })
    }

    const addApplication = userData => {
        store
            .dispatch('appApplications/addApplication', userData)
            .then(() => {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: 'User created',
                        icon: 'CheckIcon',
                        variant: 'success',
                    },
                })
                users = []
                refetchData()
            })
            .catch(() => {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: i18n.t('error'),
                        text: i18n.t('something_went_wrong'),
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                    },
                })
            })
    }

    // *===============================================---*
    // *--------- UI ---------------------------------------*
    // *===============================================---*

    const resolveUserRoleVariant = role => {
        if (role === 'subscriber') return 'primary'
        if (role === 'author') return 'warning'
        if (role === 'maintainer') return 'success'
        if (role === 'editor') return 'info'
        if (role === 'admin') return 'danger'
        return 'primary'
    }

    const resolveUserRoleIcon = role => {
        if (role === 'subscriber') return 'UserIcon'
        if (role === 'author') return 'SettingsIcon'
        if (role === 'maintainer') return 'DatabaseIcon'
        if (role === 'editor') return 'Edit2Icon'
        if (role === 'admin') return 'ServerIcon'
        return 'UserIcon'
    }

    const resolveUserStatusVariant = status => ({
        pending: COLOR_VARIANTS.LIGHT_WARNING,
        accepted: COLOR_VARIANTS.LIGHT_SUCCESS,
        rejected: COLOR_VARIANTS.LIGHT_SECONDARY
    }[status] || COLOR_VARIANTS.LIGHT_PRIMARY)

    return {
        fetchApplications,
        deleteUser,
        confirmDelete,
        hoiwaColumns,
        oiwaColumns,
        perPage,
        currentPage,
        totalUsers,
        dataMeta,
        perPageOptions,
        searchQuery,
        sortBy,
        isSortDirDesc,
        refUserListTable,
        loadMore,
        canLoadMore,
        countApplicationRequest,

        resolveUserRoleVariant,
        resolveUserRoleIcon,
        resolveUserStatusVariant,
        refetchData,

        // Extra Filters
        roleFilter,
        planFilter,
        statusFilter,
        stateFilter,
        cityFilter,
        groupFilter,
        professionFilter,
        stepFilter,
        oiwaFilter,
        seekingFilter,
        seekingStartFilter,
        seekingEndFilter,
        ageFilter,
        residencyFilter,

        // fetchStatistics
        fetchStatistics,
        // fetchApplicationRequests
        fetchApplicationRequests,
        changePage,
        changePerPage,
        totalRows,
        addApplication,
    }
}
